import { EmptyPage } from "@components/common/empty-page/empty-page";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { GroupDetailsInfoBar } from "@components/group-details/group-details-info-bar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { ProjectsTable } from "@components/table/projects/projects-table";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { BaseCompanyIdProps, GroupId } from "@custom-types/base-types";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { useFetchProjects } from "@hooks/projects/use-fetch-projects";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ProjectsMobileView } from "@pages/projects/projects-mobile-view";
import { requiredProjectTableColumns } from "@pages/projects/projects-utils";
import { ProjectsDuoIcon } from "@flat-ui";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { BulkChangeProjectStatus } from "@components/table/projects/bulk-actions/bulk-change-project-status";

interface Props extends BaseCompanyIdProps, Partial<BaseGroupDetailsProps> {
  /** ID of the group */
  groupId: GroupId;

  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;

  /** The state of projects to display (active or archived) */
  projectState: ProjectArchivingState;
}

/**
 * Contains the Projects tab of the group details - can display both active and archived projects
 */
export function GroupProjects({
  companyId,
  groupId,
  group,
  isLoading = false,
  projectState,
}: Props): JSX.Element {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));
  const { isScreenSmOrSmaller } = useMediaQueryList();

  const {
    projects,
    isFetchingProjects,
    hasFetchedFirstPage,
    nextPageCursor,
    sortBy,
    setSortBy,
    loadMoreProjects,
    totalCount,
  } = useFetchProjects({
    companyId,
    projectArchivingState: projectState,
    groupId,
  });

  const isProjectsLoading = isFetchingProjects || isLoading;
  const shouldShowEmptyPage =
    !isProjectsLoading && hasFetchedFirstPage && projects.length === 0;

  const isAnyRowSelected = selectedEntities.length !== 0;
  const shouldRenderActions = projects.length > 0;
  const shouldShowMobileView = isScreenSmOrSmaller && !shouldShowEmptyPage;
  const shouldShowDesktopView = !isScreenSmOrSmaller && !shouldShowEmptyPage;

  return (
    <>
      {!isScreenSmOrSmaller && <GroupDetailsInfoBar group={group} />}

      <EntitiesToolbar
        toolbarEntityName="Project"
        numberOfRows={totalCount}
        isLoading={isProjectsLoading}
        sortActions={
          shouldRenderActions && (
            <ProjectsSort
              key="projects-sort"
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          )
        }
        exportComponent={
          shouldRenderActions && (
            <BulkExportProjects
              companyId={companyId}
              selectedProjects={isAnyRowSelected ? selectedEntities : projects}
              subjectType="group"
              projectStatus={projectState}
              areEntitiesSelected={isAnyRowSelected}
              buttonType="standard"
            />
          )
        }
      />

      {shouldShowEmptyPage && (
        <>
          <EmptyPage
            title={`This group has no ${projectState} projects`}
            subtitle={
              projectState === ProjectArchivingState.active
                ? "You don't have any active projects connected to this group. Create a new project or add an existing active project to this group."
                : "There are no archived projects connected to this group."
            }
            icon={ProjectsDuoIcon}
          />
          <BulkChangeProjectStatus
            projectStatus={projectState}
            projects={projects}
          />
        </>
      )}

      {shouldShowMobileView && (
        <ProjectsMobileView
          projects={projects}
          sortBy={sortBy}
          isLoading={isProjectsLoading}
          projectArchivingState={projectState}
          loadMoreProjects={loadMoreProjects}
          shouldShowCreateButton={false}
        />
      )}

      {shouldShowDesktopView && (
        <ColumnPreferencesProvider
          tableKey="group-project-table"
          columns={requiredProjectTableColumns}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: [
              "name",
              "projectManager",
              "group",
              "created",
              "lastUpdated",
              "accessLevel",
            ],
          }}
        >
          <ProjectsTable
            companyId={companyId}
            projects={projects}
            isLoading={isProjectsLoading}
            nextPageCursor={nextPageCursor}
            loadMoreProjects={loadMoreProjects}
            sortBy={sortBy}
            setSortBy={setSortBy}
            groupId={groupId}
            projectArchivingState={projectState}
            totalCount={totalCount}
          />
        </ColumnPreferencesProvider>
      )}
    </>
  );
}
